import { useQuery, useMutation } from '@tanstack/react-query';
import { GlobalApi } from '@Apis';
import { queryClient } from 'src/QueryClient';
import { PolicyTypeEnum } from '../../../Policy/utils/constants';
import {
  ApprovedUsersProps,
  DeleteApprovedUsersDto,
} from '../../../../types/globalSetting';
import { useTranslation } from 'react-i18next';

const getApprovedTeamsQueryKey = (policyType: PolicyTypeEnum) => [
  'approvedTeams',
  policyType,
];

export const useApprovedTeamsQuery = (policyType: PolicyTypeEnum) => {
  return useQuery({
    queryKey: getApprovedTeamsQueryKey(policyType),
    queryFn: async () => {
      const { data } = await GlobalApi.getApprovedUsers(policyType);
      return data as ApprovedUsersProps[];
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

export const useApprovedTeamsMutations = (policyType: PolicyTypeEnum) => {
  const { t } = useTranslation(['globalSetting', 'common']);
  const queryKey = getApprovedTeamsQueryKey(policyType);

  const addTeamsMutation = useMutation({
    mutationFn: (users: Omit<ApprovedUsersProps, 'groupCompanyGuid'>[]) =>
      GlobalApi.addApprovedUsers(policyType, users),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey });
    },
    onError: () => {
      window.showNotification({
        severity: 'error',
        titleMsg: t('common.apiException.internalError'),
      });
    },
  });

  const deleteTeamsMutation = useMutation({
    mutationFn: (users: DeleteApprovedUsersDto[]) =>
      GlobalApi.deleteApprovedUsers(policyType, users),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey });
    },
    onError: () => {
      window.showNotification({
        severity: 'error',
        titleMsg: t('common.apiException.internalError'),
      });
    },
  });

  return {
    addTeamsMutation,
    deleteTeamsMutation,
  };
};
